export interface NameParts {
    fullName?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    firstNameAr?: string | null;
    lastNameAr?: string | null;
}

const clean = (value?: string | null): string => (value ?? "").trim();

const buildFrenchName = (parts: NameParts): string => {
    const full = clean(parts.fullName);
    if (full) {
        return full;
    }

    const fallback = [clean(parts.firstName), clean(parts.lastName)]
        .filter(Boolean)
        .join(" ");

    return fallback.trim();
};

const buildArabicName = (parts: NameParts): string =>
    [clean(parts.firstNameAr), clean(parts.lastNameAr)]
        .filter(Boolean)
        .join(" ")
        .trim();

export const formatNameWithArabic = (parts: NameParts): string => {
    const french = buildFrenchName(parts);
    const arabic = buildArabicName(parts);

    if (french && arabic) {
        return `${french} (${arabic})`;
    }

    if (french) {
        return french;
    }

    return arabic;
};

export const formatOfficialNameWithArabic = <T extends NameParts>(official: T): string =>
    formatNameWithArabic(official);

export const formatStaffNameWithArabic = <T extends NameParts>(staff: T): string =>
    formatNameWithArabic(staff);

export type DisplayMode = 'arabic' | 'french';

/**
 * Locale-aware person name: returns only the appropriate language name.
 * Falls back to the other language if the preferred one is absent.
 */
export function getLocalizedPersonName(
    parts: NameParts,
    mode: DisplayMode = 'arabic'
): string {
    if (mode === 'arabic') {
        const ar = buildArabicName(parts);
        return ar || buildFrenchName(parts);
    }
    return buildFrenchName(parts) || buildArabicName(parts);
}

export const getLocalizedOfficialName = (official: NameParts, mode: DisplayMode): string =>
    getLocalizedPersonName(official, mode);

export const getLocalizedPlayerName = (player: NameParts, mode: DisplayMode): string =>
    getLocalizedPersonName(player, mode);

export const getLocalizedStaffName = (staff: NameParts, mode: DisplayMode): string =>
    getLocalizedPersonName(staff, mode);

export const buildNameSearchText = (parts: NameParts): string => {
    const values = [
        clean(parts.fullName),
        clean(parts.firstName),
        clean(parts.lastName),
        clean(parts.firstNameAr),
        clean(parts.lastNameAr),
        formatNameWithArabic(parts),
    ];

    return values.filter(Boolean).join(" ");
};
